/** Simulation constants. Pure data — safe to import from anywhere. */

/** Fixed simulation rate. Never change this at runtime; determinism depends on it. */
export const TICK_RATE = 30;
export const TICK_DT = 1 / TICK_RATE;

/**
 * Map dimensions in tiles.
 *
 * 384×384 — nine times the area of the 128×128 this started at, and chosen
 * against what the simulation was measured to hold rather than against what
 * sounded generous.
 *
 * The sizing argument runs from the population, not from the picture. A town of
 * thirty thousand hands needs roughly one producer per six of them, so five
 * thousand buildings; a building plus the gap around it is about a dozen tiles,
 * so sixty thousand tiles of built ground, which is a block some 250 tiles on a
 * side. A 320-tile map would fit that with nothing to spare and no room for the
 * outskirts to be outskirts. 384 leaves the ring of forest, seams and open
 * ground the economy has to expand *into*, which is the whole shape of the
 * game.
 *
 * It is not larger than that because the flow-field Dijkstra is O(tiles log
 * tiles) and is rebuilt on a heartbeat during a siege: at 384² a rebuild of all
 * three fields is a few milliseconds, and at 512² it starts to be a visible
 * hitch on the tick it lands on. See `src/sim/flow.ts`.
 */
export const MAP_W = 384;
export const MAP_H = 384;
export const MAP_TILES = MAP_W * MAP_H;

/**
 * Hard caps for the structure-of-arrays stores.
 *
 * `MAX_UNITS` is 24576 because that is the largest round number both of the
 * game's two crowds were *measured* to hold at 30 Hz, and the next step up is
 * not. The budget is 33.3 ms a tick and the figure that matters is the
 * ninetieth percentile, not the mean — a mean under budget with a fat tail is a
 * game that stutters.
 *
 * A city under full siege, `npm run profile -- --ticks=300 --scale=N`:
 *
 *   20,000 models:  p50 22 ms   p90 30 ms
 *   24,000 models:  p50 29 ms   p90 39 ms
 *   30,000 models:  p50 39 ms   p90 51 ms
 *
 * A field battle, `npm run profile -- --ticks=300 --battle=N`, which is the
 * harder case per model — nearly everyone is in contact, targeting and swinging
 * and pressed shoulder to shoulder, where half a city is walking to work:
 *
 *   20,000 models:  p50 20 ms   p90 28 ms
 *   24,000 models:  p50 24 ms   p90 28 ms
 *   30,000 models:  p50 30 ms   p90 36 ms
 *
 * The city numbers are a deliberate worst case — every building being hit, the
 * whole population scattered, rubble everywhere — so a settled town of this
 * size sits well inside them. Thirty thousand is over budget in both, which is
 * why the cap is not there.
 *
 * Two hard ceilings sit above it and are worth knowing before anyone raises it
 * again:
 *
 *   `packHandle` packs the slot index into sixteen bits, so 65536 is the
 *   absolute limit without widening the handle.
 *   `TileMap.building` is an `Int16Array`, so `MAX_BUILDINGS` may not pass
 *   32767 without widening that layer — and the save format writes it out
 *   verbatim.
 *
 * `MAX_BUILDINGS` at 16384 is the five thousand producers a thirty-thousand
 * town needs with room for the walls, towers and civic buildings around them.
 * `MAX_PROJECTILES` scales with the number of missile troops in the air at
 * once, which at this size is thousands of volleys rather than dozens.
 */
export const MAX_UNITS = 24576;
export const MAX_PROJECTILES = 16384;
export const MAX_BUILDINGS = 16384;
export const MAX_COMPANIES = 4096;
export const MAX_EFFECTS = 512;

/** One world unit == one tile. Renderer scales by TILE_PX. */
export const TILE_PX = 40;

/**
 * Spatial hash cell size in tiles.
 *
 * Sized to the *dominant query radius*, not to the map — crowd separation runs
 * for every ground model every tick at a radius of 0.92 tiles, and targeting
 * runs for every soldier at seven to nine. Two tiles is the measured optimum
 * between them: at the reference load of five thousand models, 1 costs 23% more
 * total tick time (targeting pays for the extra rings) and 3 costs 10% more
 * (separation pays for the fuller cells).
 */
export const HASH_CELL = 2;

/**
 * How long a build phase runs before the horns sound, in seconds.
 *
 * These are long on purpose. The loop is a slow grind: you should have time to
 * lay out a quarter properly, watch the carts move, and *then* worry about the
 * dark. A player who wants to go faster presses the horn early and is paid for
 * it in Souls; a player who wants to build gets to build.
 */
export const BUILD_PHASE_SECONDS = 150;
export const FIRST_BUILD_PHASE_SECONDS = 300;
/** Build phases shorten slowly as the run goes on, but never below this. */
export const MIN_BUILD_PHASE_SECONDS = 80;

/** Villager economy. */
/**
 * Grain-equivalents one mouth wants a minute at the bottom of the ladder.
 *
 * Unchanged in value, renamed in meaning: it used to be the whole appetite and
 * is now the *first rung* of it. See `FOOD_PER_MOUTH_PER_RUNG`.
 */
export const GRAIN_PER_POP_PER_MIN = 0.42;
/**
 * Extra grain-equivalents a mouth wants for every full rung of population above
 * the first.
 *
 * **A town does not eat like a village with more people in it**, and this is the
 * settlement speaking the campaign's own language: `FOOD_PER_HOLDING` plus
 * `FOOD_PER_TIER` per rung is exactly this shape, and the argument written out
 * in `campaign/food.ts` — that the ladder must not be free — is the same
 * argument. There, a rung is a tier of holding; here it is a band of people,
 * because population *is* the settlement's ladder.
 *
 * Tuned so the change is invisible to a hamlet and unmistakable to a city:
 *
 * | pop | rungs | per mouth | a minute | before |
 * |---|---|---|---|---|
 * | 24 | 0 | 0.42 | 10.1 | 10.1 |
 * | 50 | 2 | 0.53 | 26.5 | 21.0 |
 * | 100 | 4 | 0.64 | 64.0 | 42.0 |
 * | 200 | 8 | 0.86 | 172.0 | 84.0 |
 *
 * The opening settlement — twenty-six beds, twelve full — is *within a rounding
 * error of what it always was*, deliberately: the first quarter of an hour of a
 * run is not what the owner was complaining about, and a change that made the
 * opening harder would have been a difficulty change wearing a mechanic's coat.
 */
export const FOOD_PER_MOUTH_PER_RUNG = 0.055;
/** How many mouths make a rung. */
export const POP_PER_RUNG = 25;
export const STARVATION_MORALE_DRAIN = 0.08;

/**
 * What one unit of each food is worth against the appetite, in grain.
 *
 * Grain is the unit by definition. Bread has been worth two and a half times its
 * weight since the diet landed and keeps that number exactly. The two new foods
 * sit between them, and the order here is the order the larder is eaten in —
 * richest first, which is the rule bread already followed.
 *
 * A kind that is worth more per unit is *harder ground*: bread wants an oven and
 * a standing grain surplus, salt meat wants forest that is not being felled, and
 * fish wants a bank — which every map now has, and which nothing else in the
 * catalogue has ever had a use for.
 */
export const FOOD_VALUE = { bread: 2.5, meat: 2, fish: 1.4, grain: 1 } as const;
/** The larder, in the order it is eaten. */
export const FOOD_KEYS = ['bread', 'meat', 'fish', 'grain'] as const;

/**
 * Fraction of the population cap that villagers will grow into. The rest is
 * held back for soldiers — a town that breeds into every bed cannot muster.
 */
export const VILLAGER_SHARE = 0.55;

/**
 * How long the treasury may run dry before soldiers start walking home. Long
 * enough that one bad wave is survivable; short enough that ignoring the purse
 * for a whole build phase costs an army.
 */
export const ARREARS_DESERTION_SECONDS = 26;

/** Repairing a damaged building costs this fraction of its build cost per full bar. */
export const REPAIR_COST_FRACTION = 0.3;
/** Rubble must be cleared before the ground can be built on again. */
export const RUBBLE_CLEAR_SECONDS = 6;

/**
 * Resource nodes — the trees, the rocks and the ore. See `src/data/nodes.ts`.
 *
 * `MAX_NODES` is sized off the measurement written in that file: four seeds of
 * the shipped generator produce between eleven and fourteen thousand nodes at
 * the shipped lattices. 20480 is that with room for a rockier map and for a
 * kind added later, and it costs 13 bytes a slot — 266 KiB of typed array,
 * against the unit store's several megabytes. Seeding stops rather than throws
 * if a map ever fills it, which is deterministic and degrades to "the far
 * corners of a freak map have no trees" rather than to a crash.
 */
export const MAX_NODES = 20480;

/**
 * How far from its own door a harvest building will send a worker.
 *
 * Twelve tiles, and the number is set by how much *spread* the round trip needs
 * to be worth noticing rather than by how much wood a camp ought to reach. A
 * villager walks at 1.75 tiles a second and stops a couple of tiles short at
 * each end, so the whole span from "standing in the trees" to "at the edge of
 * reach" is only about nine seconds of walking — against a shift that spends
 * the better part of twenty seconds cutting. At a radius of nine that span was
 * six seconds and the measured yield curve was flat enough to be noise; at
 * twelve it is a clean monotonic 1.45× from the best camp to the worst, which
 * is a decision a player can make.
 *
 * Measured from the building's *centre*, not from the worker, so every man on a
 * shift works the same patch and the patch runs down where the player can see
 * it happening.
 */
export const HARVEST_RADIUS = 12;

/**
 * The round trip a typical camp is assumed to make, in seconds.
 *
 * This is the calibration constant, and it is the one number in the hauling
 * system that exists to protect what already worked. A worker's chopping rate is
 * solved so that a camp whose trees are `HAUL_NOMINAL_TRAVEL` seconds away
 * produces **exactly what the old adjacency model produced**, so a settlement
 * laid out the way settlements have always been laid out earns what it always
 * earned and only *placement* changes anything.
 *
 * Two seconds, and it is set low on purpose: it makes hauling a **tax that can
 * be avoided** rather than a bonus that cannot be missed. Measured, one camp,
 * three hands, five simulated minutes, timber delivered as a share of what the
 * old model paid the same camp:
 *
 *   wood at  2–5 tiles   104%
 *   wood at  6–7 tiles   101%
 *   wood at  8–9 tiles    89–94%
 *   wood at 10–12 tiles   72–86%
 *
 * Set it at the far end of the span instead and every camp anybody could
 * actually build is paid a bonus, which is a quiet economy buff wearing a
 * feature's clothes. Here the best case is within a rounding error of what
 * shipped and the worst case is a real, visible, avoidable loss.
 *
 * It is not a difficulty dial and should not be used as one —
 * `difficulty.incomeMul` is the dial.
 */
export const HAUL_NOMINAL_TRAVEL = 2;

/**
 * How long a worker will keep walking at a node it never reaches before giving
 * up on it.
 *
 * The graceful-degradation valve, and it is here rather than in the hauling code
 * because it is the answer to a named hazard: a node behind a wall the player
 * has since closed, or on the far side of a building stamped across the only
 * path, would otherwise hold a worker walking into stone for the rest of the
 * run — and a camp whose whole shift is doing that produces nothing, for ever,
 * with no way for the player to see why. Instead the worker abandons the node,
 * the camp reads as having nothing in reach, and the old yield model takes over.
 * Slower than it should be, rather than stopped.
 */
export const HAUL_PATIENCE = 25;

/** Combat. */
export const MELEE_REACH_PAD = 0.06;
export const CORPSE_SOULS = 1;

